import { type Span, trace } from '@opentelemetry/api'
import {
  useCloseGoodsHolderMutation,
  useLazyGetGoodsHolderProcessedQuantityQuery as useGetProcessedQuantityGH,
  useOpenGoodsHolderMutation
} from 'api/goodsholder'
import { useLazyFetchInstructionQuery, useLazyFetchSilhouetteInstructionQuery } from 'api/instruction'
import { useFetchMaterialImageQuery } from 'api/material'
import { useCreateRasnMutation, useLazyGetRasnProcessedQuantityQuery as useGetProcessedQuantityRasn } from 'api/rasn'
import { useLazyGetFamilyRasnProcessedQuantityQuery as useGetProcessedQuantityFamilyRasn, useLoadFamilyRasnMutation } from 'api/rasnfamily'
import { dispatch, type RootState, useAppSelector } from 'api/redux'
import {
  useCreateUnitBySkuMutation,
  useCreateUnitMutation,
  usePrintUnitLabelMutation,
  useUpdateUnitMutation
} from 'api/unit'
import { hideSnackbar, showSnackbar } from 'components/snackbar'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  type CreateUnitBySkuRequest,
  type GoodsHolderData,
  GradeKey,
  LabelType,
  type PrintLabelResponse,
  type RASN,
  type RASNFamily,
  Screen,
  type Unit,
  Warning
} from 'types'
import { isBrimley, isFlight, isImpact } from 'utils/dcContext'
import { checkIfLabelIsEnabled } from 'utils/printconfig'
import { useApiHandler } from 'utils/useApiHandler'

const defaultUnit: Unit = {
  unitId: '',
  countryOfOrigin: '',
  styleDescription: '',
  productCode: '',
  sizeCode: '',
  upc: '',
  division: '',
  silhouette: '',
  grade: '',
  gradeReason: '',
  gradeReasonCode: '',
  refundApproval: null,
  hashable: false,
  mheConveyable: null,
  quantity: 0,
  qualityCode: '',
  isegCode: '',
  toteBarcode: null,
  status: '',
  imageUrl: '',
  lpn: ''
}

export const useUnitHandlingApi = () => {
  const { t } = useTranslation()

  const [displayFailure, setDisplayFailure] = useState(false)
  const [unitData, setUnitData] = useState<Unit>(defaultUnit)
  const [rasnData, setRasnData] = useState<RASN>()
  const [goodsHolder, setGoodsHolder] = useState<string>()
  const [goodsHolderTrackingId, setGoodsHolderTrackingId] = useState<string | undefined>()
  const [activeScreen, setActiveScreen] = useState<Screen>(Screen.SCAN_RLPN_RASN)
  const [processedQuantity, setProcessedQuantity] = useState<number>(0)
  const [warning, setWarning] = useState<Warning | null>(null)
  const [returnsWindowWarningShown, setReturnsWindowWarningShown] = useState(false)

  const [rasnFamilyData, setRasnFamilyData] = useState<RASNFamily>()

  const [openGoodsHolderMutation] = useOpenGoodsHolderMutation()
  const [closeGoodsHolderMutation] = useCloseGoodsHolderMutation()
  const [createRasnMutation] = useCreateRasnMutation()
  const [loadFamilyRasnMutation] = useLoadFamilyRasnMutation()
  const [createUnitMutation] = useCreateUnitMutation()
  const [createUnitBySkuMutation] = useCreateUnitBySkuMutation()
  const [updateUnitMutation] = useUpdateUnitMutation()
  const [reprintLabel] = usePrintUnitLabelMutation()

  const [triggerGetGoodsHolderProcessedQuantity, { data: processedQuantityGoodsholder }] = useGetProcessedQuantityGH()
  const [triggerGetRasnProcessedQuantity, { data: processedQuantityRasn }] = useGetProcessedQuantityRasn()
  const [triggerGetFamilyRasnProcessedQuantity, { data: processedQuantityFamilyRasn }] = useGetProcessedQuantityFamilyRasn()
  const [triggerGetInstructionData, { data: gradeInstruction, isLoading: isInstructionsLoading, isError: isInstructionsError }] = useLazyFetchInstructionQuery()
  const [triggerGetSilhouetteInstruction, {
    data: silhouetteInstruction,
    isLoading: isSilhouetteInstructionLoading,
    originalArgs: silhouetteInstructionArgs
  }] = useLazyFetchSilhouetteInstructionQuery()

  const tracer = trace.getTracer('rasn-processing')
  const [currentRasnSpan, setCurrentRasnSpan] = useState<Span | null>(null)
  const [currentUnitSpan, setCurrentUnitSpan] = useState<Span | null>(null)
  const [currentScreenSpan, setCurrentScreenSpan] = useState<Span | null>(null)

  const { data: fetchedImage, isLoading: isImageLoading } = useFetchMaterialImageQuery(unitData?.productCode, { skip: !unitData.productCode })

  const printConfig = useAppSelector((state: RootState) => state.application.printConfig)

  useEffect(() => {
    if (fetchedImage?.image) {
      setUnitData((prevState) => ({ ...prevState, imageUrl: fetchedImage.image }))
      return
    } else {
      if (isImageLoading) {
        return
      }
    }
    // Always set default image if not loading and image is missing
    setUnitData((prevState) => ({ ...prevState, imageUrl: '/images/image-unavailable.jpg' }))
  }, [isImageLoading, fetchedImage])

  useEffect(() => {
    if (isInstructionsError) {
      setWarning(new Warning(t('warnings.special_instruction_title'), t('warnings.special_instruction_message')))
    }
  }, [isInstructionsError])

  const overlayFailure = () => {
    setDisplayFailure(true)
    setTimeout(() => { setDisplayFailure(false) }, 999)
  }

  useEffect(() => {
    if (currentScreenSpan) {
      currentScreenSpan.end()
    }
    const newSpan = tracer.startSpan(`screen-${Screen[activeScreen]}`)
    setCurrentScreenSpan(newSpan)
    // Cleanup function when component unmounts
    return () => {
      newSpan.end()
    }
  }, [activeScreen])

  useEffect(() => {
    if (activeScreen === Screen.SCAN_UNIT) {
      if (goodsHolder) {
        triggerGetGoodsHolderProcessedQuantity(goodsHolder)
      } else if (rasnFamilyData) {
        triggerGetFamilyRasnProcessedQuantity(rasnFamilyData.number)
      } else if (rasnData) {
        triggerGetRasnProcessedQuantity(rasnData.number)
      }
      if (rasnData?.returnsEndDateExceeded && !returnsWindowWarningShown) {
        setWarning(new Warning(t('warnings.returns_window_title'), t('warnings.returns_window_message')))
        setReturnsWindowWarningShown(true)
      }
    }
  }, [activeScreen, goodsHolder, rasnData, rasnFamilyData, triggerGetGoodsHolderProcessedQuantity, triggerGetRasnProcessedQuantity, triggerGetFamilyRasnProcessedQuantity, returnsWindowWarningShown])

  useEffect(() => {
    if (processedQuantityRasn && rasnData && !goodsHolder) {
      setProcessedQuantity(processedQuantityRasn.totalHandledUnits)
    }
  }, [processedQuantityRasn, rasnData, goodsHolder])

  useEffect(() => {
    if (processedQuantityGoodsholder && goodsHolder) {
      setProcessedQuantity(processedQuantityGoodsholder.processedQuantity)
    }
  }, [processedQuantityGoodsholder, goodsHolder])

  useEffect(() => {
    if (processedQuantityFamilyRasn && rasnFamilyData && !goodsHolder) {
      setRasnFamilyData(processedQuantityFamilyRasn)
      setProcessedQuantity(processedQuantityFamilyRasn.totalHandledUnits)
    }
  }, [processedQuantityFamilyRasn])
  const openRlpnContext = useApiHandler({
    apiFunction: async (rlpn: string) => await openGoodsHolderMutation(rlpn).unwrap(),
    successAction: (result: GoodsHolderData) => {
      setGoodsHolder(result.lpn)
      setGoodsHolderTrackingId(result.trackingId ?? undefined)
      if (result.rasnFamily) {
        setRasnFamilyData(result.rasnFamily)
      } else {
        setRasnData(result.rasn)
      }
      setProcessedQuantity(result.processedQuantity)
      setReturnsWindowWarningShown(false)
      setActiveScreen(Screen.SCAN_UNIT)
    },
    failureAction: (error?: any) => {
      overlayFailure()
      if (error.status === 404 || error.status === 423) {
        throw error
      }
      if (error.status === 400) {
        dispatch(showSnackbar(error.data, 'error'))
      }
    }
  })

  const createRasnContext = useApiHandler({
    apiFunction: async (rasnId: string) => await createRasnMutation(rasnId).unwrap(),
    successAction: (result: RASN) => {
      setRasnData(result)
      setProcessedQuantity(result.totalHandledUnits)
      setReturnsWindowWarningShown(false)
      setActiveScreen(Screen.SCAN_UNIT)
      const newSpan: Span = tracer.startSpan(`rasn-${result.number}`)
      newSpan.setAttribute('rasn', result.number)
      setCurrentRasnSpan(newSpan)
    },
    failureAction: (error?: any) => {
      if (error.status === 409) {
        dispatch(showSnackbar(`${error.data.message as string}`, 'error'))
      }
      overlayFailure()
      if (error.status === 404 || error.status === 423) {
        throw error
      }
    }
  })

  const createFamilyRasnContext = useApiHandler({
    apiFunction: async (rasnFamilyId: string) => await loadFamilyRasnMutation(rasnFamilyId).unwrap(),
    successAction: (result: RASNFamily) => {
      setRasnFamilyData(result)
      setProcessedQuantity(result.totalHandledUnits)
      setReturnsWindowWarningShown(false)
      setActiveScreen(Screen.SCAN_UNIT)
      const newSpan: Span = tracer.startSpan(`rasn-family-${result.number}`)
      newSpan.setAttribute('rasnFamily', result.number)
      setCurrentRasnSpan(newSpan)
    },
    failureAction: (error?: any) => {
      if (error.status === 409) {
        dispatch(showSnackbar(`${error.data.message as string}`, 'error'))
      }
      overlayFailure()
      if (error.status === 404 || error.status === 423) {
        throw error
      }
    }
  })

  const createUnitSuccessAction = (result: Unit) => {
    triggerGetInstructionData({
      productCode: result.productCode,
      channel: rasnData?.channel ?? rasnFamilyData?.channel,
      shipTo: rasnData?.shipTo,
      country: rasnData?.country
    })
    if (result.silhouette && result.silhouette.trim().length > 0) {
      triggerGetSilhouetteInstruction(result.silhouette)
    }
    dispatch(hideSnackbar())
    setUnitData(result)
    setActiveScreen(Screen.GRADING)
    const newSpan: Span = tracer.startSpan(`unit-${result.unitId}`)
    newSpan.setAttribute('upc', result.upc)
    setCurrentUnitSpan(newSpan)
    currentRasnSpan?.setAttribute('unitsProcessed', result.productCode)
  }

  const unitFailureAction = (error?: any) => {
    if (!error) {
      overlayFailure()
      return
    }

    const { status, data } = error
    switch (status) {
      case 400:
        overlayFailure()
        dispatch(showSnackbar(data.message ?? data ?? t('snackbar.error_general'), 'error'))
        break
      case 409:
        if (data.message.includes('cancelled')) {
          setWarning(new Warning(
            data.message,
            `${t('warnings.rasn_cancelled_message_line_one')}
            ${t('warnings.rasn_cancelled_message_line_two')}`,
            () => { resetToScanRlpnRasn() }
          ))
        } else {
          if (data.message.includes('max number of units allowed for this RASN')) {
            setWarning(new Warning(
              t('warnings.tolerance_exceeded_title'),
              `${t('warnings.tolerance_exceeded_message_line_one')}
              ${t('warnings.tolerance_exceeded_message_line_two')}`,
              () => { resetToScanRlpnRasn() }
            ))
          }
          if (data.message.includes('max number of units allowed for this material')) {
            setWarning(new Warning(
              t('warnings.material_limit_exceeded_title'),
              `${t('warnings.material_limit_exceeded_message_line_one')}
              ${t('warnings.material_limit_exceeded_message_line_two')}`
            ))
          }
        }
        break
      case 423:
        setWarning(new Warning(
          data.message,
          `${t('warnings.rasn_closed_message_line_one')}
          ${t('warnings.rasn_closed_message_line_two')}`,
          () => { resetToScanRlpnRasn() }
        ))
        break
      default:
        overlayFailure()
        throw error
    }
  }

  const createUnitContext = useApiHandler({
    apiFunction: async (upc: string) => await createUnitMutation(
      { upc, lpn: goodsHolder ?? '' }
    ).unwrap(),
    successAction: createUnitSuccessAction,
    failureAction: unitFailureAction
  })

  const createUnitBySkuContext = useApiHandler({
    apiFunction: async (requestWithLpn: CreateUnitBySkuRequest) => await createUnitBySkuMutation(
      { ...requestWithLpn, lpn: goodsHolder ?? null }
    ).unwrap(),
    successAction: createUnitSuccessAction,
    failureAction: unitFailureAction
  })

  const updateUnitGradeContext = useApiHandler({
    apiFunction: async (unit: Unit) => await updateUnitMutation(unit).unwrap(),
    successAction: (unit: Unit) => {
      setUnitData((prevState) => ({ ...unit, imageUrl: prevState.imageUrl }))
      dispatch(showSnackbar(t('snackbar.success_graded', { grade: getSimpleGrade(unit.grade) }), 'success', 10000))
      switch (unit.grade) {
        case GradeKey.A_GRADE:
        case GradeKey.B_GRADE:
        case GradeKey.S_GRADE:
        case GradeKey.HASH:
          if (isBrimley) {
            setActiveScreen(Screen.SCAN_TOTE)
            break
          } else {
            setActiveScreen(Screen.REWORK)
            break
          }
        case GradeKey.C_GRADE:
          handleCGradeFlow(unit)
          break
        default:
          overlayFailure()
          break
      }
    },
    failureAction: unitFailureAction
  })

  const getSimpleGrade = (grade: string) => {
    switch (grade) {
      case 'A_GRADE':
        return t('grading.a')
      case 'B_GRADE':
        return t('grading.b')
      case 'C_GRADE':
        return t('grading.c')
      case 'S_GRADE':
        return t('grading.s')
      default:
        return grade
    }
  }

  const handleCGradeFlow = (unit: Unit) => {
    if (isBrimley || isFlight || isImpact) {
      if (unit.refundApproval) {
        setActiveScreen(Screen.SCAN_TOTE)
      } else {
        setActiveScreen(Screen.CGRADE_COMPLETION)
      }
    } else {
      if (checkIfLabelIsEnabled(printConfig, LabelType.CGrade)) {
        dispatch(showSnackbar(t('snackbar.printing_c_grade'), 'info'))
        printLabelContext.handleApiCall(LabelType.CGrade)
      }
      setActiveScreen(Screen.CGRADE_COMPLETION)
    }
  }

  const printLabelContext = useApiHandler({
    apiFunction: async (labelType: LabelType) => await reprintLabel({ unitId: unitData.unitId, labelType }).unwrap(),
    successAction: (response: PrintLabelResponse) => {
      const message = response.status === 'OK' ? t('snackbar.success_printing') : t('snackbar.error_printing')
      const status = response.status === 'OK' ? 'success' : 'error'
      dispatch(showSnackbar(message, status))
    },
    failureAction: () => {
      dispatch(showSnackbar(t('snackbar.error_printing'), 'error'))
    }
  })

  const resetUnitHandlingFlow = () => {
    localStorage.removeItem('unitId')
    setUnitData(defaultUnit)
    currentUnitSpan?.end()
    setActiveScreen(Screen.SCAN_UNIT)
  }

  const updateUnitToteScanContext = useApiHandler({
    apiFunction: async (unit: Unit) => await updateUnitMutation(unit).unwrap(),
    successAction: (unit: Unit) => {
      dispatch(showSnackbar(t('snackbar.success_scanned_into_tote'), 'success', 10000))
      resetUnitHandlingFlow()
    },
    failureAction: overlayFailure
  })

  const confirmCgradeComplete = useApiHandler({
    apiFunction: async (unit: Unit) => await updateUnitMutation(unit).unwrap(),
    successAction: (unit: Unit) => {
      dispatch(showSnackbar(t('snackbar.success_printing_c_grade'), 'success'))
      resetUnitHandlingFlow()
    },
    failureAction: overlayFailure
  })

  const closeGoodsHolderContext = useApiHandler({
    apiFunction: async (lpnNumber: string) => await closeGoodsHolderMutation(lpnNumber).unwrap(),
    successAction: () => {
      resetToScanRlpnRasn()
    },
    failureAction: overlayFailure
  })

  const resetToScanRlpnRasn = () => {
    dispatch(hideSnackbar())
    setActiveScreen(Screen.SCAN_RLPN_RASN)
    setGoodsHolder(undefined)
    setGoodsHolderTrackingId(undefined)
    setRasnData(undefined)
    setRasnFamilyData(undefined)
    setUnitData(defaultUnit)
    setProcessedQuantity(0)
    setReturnsWindowWarningShown(false)
    currentRasnSpan?.end()
    currentUnitSpan?.end()
    currentScreenSpan?.end()
  }

  const resetUnitDataToScanned = () => {
    setUnitData((prevData) => ({
      ...prevData,
      grade: '',
      gradeReason: '',
      gradeReasonCode: '',
      refundApproval: null,
      hashable: false,
      quantity: 0,
      qualityCode: '',
      isegCode: '',
      toteBarcode: null,
      status: 'SCANNED'
    }))
  }

  const trimmedSilhouette = unitData.silhouette?.trim() ?? ''
  const currentSilhouetteInstruction =
    Boolean(trimmedSilhouette) &&
    silhouetteInstructionArgs === trimmedSilhouette &&
    !isSilhouetteInstructionLoading
      ? silhouetteInstruction ?? undefined
      : undefined

  return {
    unitData,
    resetUnitDataToScanned,
    rasnData,
    setRasnData,
    rasnFamilyData,
    goodsHolder,
    goodsHolderTrackingId,
    setGoodsHolder,
    activeScreen,
    setActiveScreen,
    processedQuantity,
    openRlpnContext,
    createRasnContext,
    createFamilyRasnContext,
    createUnitContext,
    createUnitBySkuContext,
    updateUnitGradeContext,
    updateUnitToteScanContext,
    printLabelContext,
    confirmCgradeComplete,
    closeGoodsHolderContext,
    resetUnitHandlingFlow,
    resetToScanRlpnRasn,
    displayFailure,
    gradeInstruction,
    isInstructionsLoading,
    silhouetteInstruction: currentSilhouetteInstruction,
    isSilhouetteInstructionLoading: Boolean(trimmedSilhouette) && isSilhouetteInstructionLoading,
    warning,
    setWarning
  }
}
